import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { CTASection } from "../components/shared/CTASection";
import { BLOG, IMAGES } from "../data/content";

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG.find((p) => p.slug === slug);
  const others = BLOG.filter((p) => p.slug !== slug).slice(0, 2);

  if (!post) {
    return (
      <div data-testid="blog-post-notfound" className="mx-auto max-w-3xl px-5 py-32 text-center">
        <h1 className="font-display text-3xl font-extrabold text-navy">Insight not found</h1>
        <Link to="/insights" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
          <ArrowLeft className="h-4 w-4" /> Back to Insights
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page">
      <section className="relative overflow-hidden bg-navy text-white">
        <img src={IMAGES[post.image]} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/80 to-navy/50" />
        <div className="grain relative mx-auto max-w-3xl px-5 py-24 lg:py-32">
          <Reveal>
            <Link to="/insights" data-testid="post-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/70 hover:text-aero">
              <ArrowLeft className="h-4 w-4" /> Insights
            </Link>
            <div className="mt-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-wide text-aero">
              <span>{post.category}</span><span className="text-white/50">· {post.date} · {post.readTime}</span>
            </div>
            <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl text-balance">{post.title}</h1>
          </Reveal>
        </div>
      </section>

      <article className="bg-background py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-5">
          <Reveal>
            <div className="space-y-6 text-base leading-relaxed text-steel lg:text-lg">
              {post.body.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </Reveal>
        </div>
      </article>

      <section className="border-t border-border bg-secondary py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-navy">More insights</h2>
          <div className="mt-8 grid gap-8 md:grid-cols-2">
            {others.map((o) => (
              <Link key={o.slug} to={`/insights/${o.slug}`} data-testid={`related-${o.slug}`} className="group flex overflow-hidden rounded-2xl border border-border bg-white transition-transform hover:-translate-y-1 hover:shadow-lg">
                <div className="relative w-40 shrink-0"><img src={IMAGES[o.image]} alt={o.title} className="absolute inset-0 h-full w-full object-cover" /></div>
                <div className="flex flex-1 flex-col justify-center p-6">
                  <span className="text-xs font-semibold uppercase tracking-wide text-aero">{o.category}</span>
                  <h3 className="mt-2 font-display text-base font-bold text-navy">{o.title}</h3>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand transition-transform group-hover:translate-x-1">Read <ArrowRight className="h-4 w-4" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
